import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Share, MoreVertical, PlusSquare, Smartphone } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";

const KEY = "cpenz_install_hint";

function platform() {
  const ua = navigator.userAgent || "";
  if (/iphone|ipad|ipod/i.test(ua)) return "ios";
  if (/android/i.test(ua)) return "android";
  return null;
}

/** Small "Add cpenz to your Home Screen" hint on mobile browsers (not when already installed). Shown once per device. */
export function InstallHint() {
  const { lang } = useLang();
  const [os, setOs] = useState(null);
  const [open, setOpen] = useState(false);
  const [deferred, setDeferred] = useState(null);

  useEffect(() => {
    const standalone = window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
    const p = platform();
    if (!p || standalone || localStorage.getItem(KEY)) return;
    setOs(p);
    const onPrompt = (e) => { e.preventDefault(); setDeferred(e); };
    window.addEventListener("beforeinstallprompt", onPrompt);
    const t = setTimeout(() => setOpen(true), 9000);
    return () => { clearTimeout(t); window.removeEventListener("beforeinstallprompt", onPrompt); };
  }, []);

  const close = () => { localStorage.setItem(KEY, "1"); setOpen(false); };
  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    await deferred.userChoice.catch(() => {});
    close();
  };
  const it = lang === "IT";

  return (
    <AnimatePresence>
      {open && os && (
        <div className="fixed inset-0 z-[75] flex items-center justify-center px-4 pointer-events-none md:hidden" data-testid="install-hint-layer">
        <motion.div initial={{ opacity: 0, scale: 0.92, y: 16 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95, y: 10 }} transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          className="pointer-events-auto relative w-full max-w-sm rounded-3xl bg-[#0b0f1a] text-white p-4 pr-11 shadow-[0_30px_80px_-30px_rgba(11,15,26,0.7)]" role="dialog" aria-label="Install" data-testid="install-hint">
          <button onClick={close} aria-label={it ? "Chiudi" : "Close"} data-testid="install-hint-close" className="absolute right-3 top-3 h-8 w-8 rounded-full bg-white/10 inline-flex items-center justify-center text-white/80"><X size={15} /></button>
          <div className="flex items-start gap-3">
            <img src="/apple-touch-icon.png" alt="" className="h-11 w-11 rounded-xl shrink-0" />
            <div className="min-w-0">
              <p className="text-sm font-semibold">{it ? "Aggiungi cpenz alla schermata Home" : "Add cpenz to your Home Screen"}</p>
              <p className="mt-0.5 text-xs text-white/60">{it ? "Si apre come un'app, a schermo intero, in un tocco." : "Opens like an app, full screen, in one tap."}</p>
              {os === "ios" ? (
                <ol className="mt-3 space-y-1.5 text-xs text-white/85" data-testid="install-steps-ios">
                  <li className="flex items-center gap-2"><span className="h-5 w-5 rounded-full bg-white/10 inline-flex items-center justify-center text-[10px] font-bold">1</span>{it ? "Tocca" : "Tap"} <Share size={13} className="text-[#a78bfa]" /> {it ? "Condividi in Safari" : "Share in Safari"}</li>
                  <li className="flex items-center gap-2"><span className="h-5 w-5 rounded-full bg-white/10 inline-flex items-center justify-center text-[10px] font-bold">2</span>{it ? "Scegli" : "Choose"} <PlusSquare size={13} className="text-[#a78bfa]" /> {it ? "“Aggiungi a Home”" : "“Add to Home Screen”"}</li>
                  <li className="flex items-center gap-2"><span className="h-5 w-5 rounded-full bg-white/10 inline-flex items-center justify-center text-[10px] font-bold">3</span>{it ? "Conferma con “Aggiungi”" : "Confirm with “Add”"}</li>
                </ol>
              ) : deferred ? (
                <button onClick={install} data-testid="install-hint-install" className="mt-3 inline-flex items-center gap-2 rounded-full bg-white text-[#0b0f1a] px-4 h-9 text-xs font-semibold"><Smartphone size={14} /> {it ? "Installa cpenz" : "Install cpenz"}</button>
              ) : (
                <ol className="mt-3 space-y-1.5 text-xs text-white/85" data-testid="install-steps-android">
                  <li className="flex items-center gap-2"><span className="h-5 w-5 rounded-full bg-white/10 inline-flex items-center justify-center text-[10px] font-bold">1</span>{it ? "Tocca" : "Tap"} <MoreVertical size={13} className="text-[#a78bfa]" /> {it ? "il menu di Chrome" : "the Chrome menu"}</li>
                  <li className="flex items-center gap-2"><span className="h-5 w-5 rounded-full bg-white/10 inline-flex items-center justify-center text-[10px] font-bold">2</span>{it ? "Scegli “Aggiungi a schermata Home” o “Installa app”" : "Choose “Add to Home screen” or “Install app”"}</li>
                  <li className="flex items-center gap-2"><span className="h-5 w-5 rounded-full bg-white/10 inline-flex items-center justify-center text-[10px] font-bold">3</span>{it ? "Conferma" : "Confirm"}</li>
                </ol>
              )}
            </div>
          </div>
        </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
